import React, { useState, useRef } from "react";
import { api, errMsg } from "@/lib/api";
import { toast } from "sonner";
import { UploadCloud, X, Loader2, ImagePlus } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const toAbs = (u) => (u && u.startsWith("/") ? `${BACKEND}${u}` : u);

export function ImageUpload({ value, onChange, multiple = false, label, hint, previewClass = "h-44", testid = "image-upload" }) {
  const [busy, setBusy] = useState(false);
  const [drag, setDrag] = useState(false);
  const inputRef = useRef(null);

  const uploadOne = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
    return toAbs(data.url);
  };

  const handleFiles = async (fileList) => {
    const imgs = Array.from(fileList || []).filter((f) => f.type.startsWith("image/"));
    if (!imgs.length) return;
    setBusy(true);
    try {
      const urls = [];
      for (const f of imgs) urls.push(await uploadOne(f));
      if (multiple) onChange([...(value || []), ...urls]);
      else onChange(urls[0]);
      toast.success(urls.length > 1 ? `${urls.length} images uploaded` : "Image uploaded");
    } catch (e) {
      toast.error(errMsg(e.response?.data?.detail) || "Upload failed");
    } finally {
      setBusy(false);
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDrag(false);
    if (e.dataTransfer?.files?.length) handleFiles(e.dataTransfer.files);
  };

  const onPaste = (e) => {
    const items = e.clipboardData?.items;
    if (!items) return;
    const files = [];
    for (const it of items) if (it.type.startsWith("image/")) { const f = it.getAsFile(); if (f) files.push(f); }
    if (files.length) { e.preventDefault(); handleFiles(files); }
  };

  const removeSingle = () => onChange("");
  const removeAt = (i) => onChange((value || []).filter((_, idx) => idx !== i));

  const dropzone = (
    <div
      data-testid={`${testid}-dropzone`}
      tabIndex={0}
      onClick={() => inputRef.current?.click()}
      onPaste={onPaste}
      onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
      onDragLeave={() => setDrag(false)}
      onDrop={onDrop}
      className={`cursor-pointer rounded-2xl border-2 border-dashed transition-colors flex flex-col items-center justify-center text-center px-4 py-6 outline-none ${
        drag ? "border-orange-500 bg-orange-50" : "border-slate-300 hover:border-orange-400 bg-slate-50 focus:border-orange-400"
      }`}
    >
      {busy ? (
        <><Loader2 className="w-6 h-6 text-orange-500 animate-spin mb-2" /><span className="text-sm text-slate-500">Uploading…</span></>
      ) : (
        <>
          <UploadCloud className="w-7 h-7 text-orange-500 mb-2" strokeWidth={1.5} />
          <p className="text-sm font-medium text-slate-700">Drag & drop, paste, or click to upload</p>
          <p className="text-xs text-slate-400 mt-0.5">{hint || "PNG, JPG, GIF or WEBP · up to 8MB"}</p>
        </>
      )}
      <input ref={inputRef} type="file" accept="image/*" multiple={multiple} className="hidden"
        data-testid={`${testid}-input`} onChange={(e) => { handleFiles(e.target.files); e.target.value = ""; }} />
    </div>
  );

  return (
    <div>
      {label && <label className="text-sm font-medium text-slate-700 block mb-1.5">{label}</label>}
      {!multiple && value ? (
        <div className="relative group inline-block w-full">
          <img src={value} alt="" className={`w-full ${previewClass} object-cover rounded-2xl border border-slate-200`} />
          <button type="button" data-testid={`${testid}-remove`} onClick={removeSingle}
            className="absolute top-2 right-2 w-8 h-8 rounded-full bg-slate-900/70 text-white flex items-center justify-center hover:bg-red-500 transition-colors">
            <X className="w-4 h-4" />
          </button>
          <button type="button" onClick={() => inputRef.current?.click()}
            className="absolute bottom-2 right-2 bg-white/90 text-slate-700 text-xs font-medium rounded-full px-3 py-1 flex items-center gap-1 hover:bg-white">
            <ImagePlus className="w-3.5 h-3.5" /> Replace
          </button>
          <input ref={inputRef} type="file" accept="image/*" className="hidden"
            data-testid={`${testid}-input`}
            onChange={(e) => { handleFiles(e.target.files); e.target.value = ""; }} />
        </div>
      ) : multiple ? (
        <div className="space-y-3">
          {value?.length > 0 && (
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-2" data-testid={`${testid}-grid`}>
              {value.map((url, i) => (
                <div key={i} className="relative group">
                  <img src={url} alt="" className="aspect-square object-cover rounded-xl border border-slate-200" />
                  <button type="button" data-testid={`${testid}-remove-${i}`} onClick={() => removeAt(i)}
                    className="absolute top-1 right-1 w-6 h-6 rounded-full bg-slate-900/70 text-white flex items-center justify-center hover:bg-red-500">
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
            </div>
          )}
          {dropzone}
        </div>
      ) : (
        dropzone
      )}
    </div>
  );
}
